import { ContestStatus } from '@betterpool/api-types/contests-service';

import { MyContestV2ItemSchema } from '~/services/contests/contests-v2.service.api';
import { logError } from '~/common/utils/log';

import type { MyUserContest } from '../types/userContest';

import unknownContestTypeAltText from './unknownContestTypeAltText';

const V2_TO_V1_STATUS: Record<string, ContestStatus> = {
  scheduled: ContestStatus.SCHEDULED,
  in_progress: ContestStatus.IN_PROGRESS,
  finished: ContestStatus.FINISHED,
  finalized: ContestStatus.FINALIZED,
  cancelled: ContestStatus.CANCELLED,
};

const getV1Status = (isStarted: boolean, status: string): ContestStatus => {
  if (!isStarted) {
    return ContestStatus.SCHEDULED;
  }

  return V2_TO_V1_STATUS[status] ?? ContestStatus.IN_PROGRESS;
};

const getUserSegmentedPayouts = (cents: number | null | undefined): number | undefined =>
  cents || undefined;

const parseMyContestsV2 = (contests: unknown[]): MyUserContest[] =>
  contests.reduce<MyUserContest[]>((parsed, rawItem) => {
    const result = MyContestV2ItemSchema.safeParse(rawItem);
    if (result.success === false) {
      logError('parseMyContestsV2: skipping invalid v2 contest item', result.error, rawItem);
      return parsed;
    }

    const item = result.data;
    const [league] = item.contest.leagues ?? [];
    const leaderboard = (() => {
      if (item.leaderboard?.type === 'ranked_leaderboard') {
        return {
          positions: item.leaderboard.positions.map((position) => ({
            entryId: position.entryId,
            rank: position.rank - 1,
          })),
          totalSpots: item.leaderboard.totalSpots,
          payoutSpots: item.leaderboard.payoutSpots,
          userPayout: item.leaderboard.userPayoutCents,
          userSegmentedPayouts: getUserSegmentedPayouts(item.leaderboard.userSegmentedPayoutsCents),
          isUserEliminated: item.leaderboard.isUserEliminated === true,
        };
      }

      if (item.leaderboard?.type === 'survivor_leaderboard') {
        return {
          positions: [],
          totalSpots: item.leaderboard.contestTotalEntries,
          payoutSpots: item.leaderboard.contestAliveEntries,
          userPayout: item.leaderboard.userPayoutCents,
          userSegmentedPayouts: getUserSegmentedPayouts(item.leaderboard.userSegmentedPayoutsCents),
          isUserEliminated: item.leaderboard.isUserEliminated === true,
        };
      }

      return undefined;
    })();

    parsed.push({
      id: item.contest.id,
      commissioner: item.commissioner,
      league,
      leagues: item.contest.leagues,
      name: item.contest.name,
      start_date: item.contest.startDate ? new Date(item.contest.startDate) : null,
      settled_date: item.contest.settledDate ? new Date(item.contest.settledDate) : undefined,
      pick_lock_date: item.nextPickLockDate ? new Date(item.nextPickLockDate) : null,
      entries: {
        filled: item.entries?.filled,
        max: item.entries?.max,
        user: item.entries?.user,
        user_any_stake: item.entries?.userAnyStake,
        max_per_user: item.entries?.maxPerUser,
        is_unlimited_entries: item.entries?.isUnlimitedEntries,
      },
      entry_fee: item.contest.entryFeeCents,
      prize_pool: item.contest.prizePoolCents,
      contest_type: item.contest.contestType,
      contest_type_alt_text: unknownContestTypeAltText(
        item.contest.contestType,
        item.contest.contestTypeAltText
      ),
      status: getV1Status(item.contest.isStarted, item.contest.status),
      survivor:
        item.leaderboard?.type === 'survivor_leaderboard'
          ? {
              userAliveEntries: item.leaderboard.userAliveEntries,
              aliveEntries: item.leaderboard.contestAliveEntries,
            }
          : undefined,
      missingPicksEntryIds: [],
      injuredPicksEntryIds: [],
      leaderboard,
      alerts: item.alerts,
      entitySource: item.contest.entitySource,
      isEntertainmentOnly: item.contest.isEntertainmentOnly,
      game_center_url: item.urls?.gameCenterUrl,
      share_url: item.urls?.shareUrl,
    } as unknown as MyUserContest);

    return parsed;
  }, []);

export default parseMyContestsV2;
